/**
 * Account API service for user account management.
 * Handles account deletion and related operations.
 */

import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

export interface DeleteAccountResponse {
  message: string;
  deletedItems: {
    documents: number;
    projects: number;
    jobs: number;
    analysis: number;
    audioFiles: number;
    transcripts: number;
    documentFiles: number;
    templates: number;
    subscription: number;
  };
}

class AccountApiService extends BaseApiService {
  constructor() {
    super(API_URL, 'AccountApi');
  }

  /**
   * Delete the user's account and all associated data.
   * This operation is IRREVERSIBLE.
   */
  async deleteAccount(): Promise<DeleteAccountResponse> {
    this.log.info('Initiating account deletion', { action: 'deleteAccount' });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/user/account`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ confirmDelete: true }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to delete account', new Error(data.error || `HTTP ${response.status}`), {
        action: 'deleteAccount',
        status: response.status,
      });
      throw new Error(data.error || 'Failed to delete account');
    }

    const data: DeleteAccountResponse = await response.json();
    this.log.info('Account deleted successfully', { action: 'deleteAccount', deletedItems: data.deletedItems });
    return data;
  }
}

// Export singleton instance
export const accountApi = new AccountApiService();
